import Big from 'big.js';

export enum Currency {
	RUB = 'RUB',
	KZT = 'KZT',
	USD = 'USD',
	EUR = 'EUR',
	USDT = 'USDT',
}

export const currencySymbolByCode: Record<Currency, string> = {
	[Currency.RUB]: '₽',
	[Currency.KZT]: '₸',
	[Currency.USD]: '$',
	[Currency.EUR]: '€',
	[Currency.USDT]: 'USDT',
};

interface FormatOptions {
	symbolCode?: keyof typeof currencySymbolByCode;
	precision?: number;
}

const toNumber = (value: string | number): number =>
	typeof value === 'string' ? Number(value.replace(/[^\d.,-]/gi, '').replace(',', '.')) : value;

const convertToSubunits = (value: number): number => new Big(value ?? 0).mul(100).toNumber();
const convertToBaseCurrency = (value: number): number => new Big(value ?? 0).div(100).toNumber();

function format(value: number | string, {symbolCode = Currency.RUB, precision = 2}: FormatOptions = {}) {
	const _value = typeof value === 'number' ? value : toNumber(value);
	const sign = _value < 0 ? '-' : '';
	const symbol = currencySymbolByCode[symbolCode];
	const positiveValue = Math.abs(_value);
	const numericString = String(Number(positiveValue.toFixed(precision)));
	const [integerPart, restPart = ''] = numericString.split('.');

	let transformedIntegerPart = '';

	for (let i = 0; i < integerPart.length; i += 1) {
		const v = integerPart[integerPart.length - 1 - i];
		transformedIntegerPart =
			i !== 0 && i % 3 === 0 ? `${v}\u00A0${transformedIntegerPart}` : `${v}${transformedIntegerPart}`;
	}

	const clearedValue = sign + transformedIntegerPart + (restPart && ',') + restPart;
	const computedValue = `${clearedValue}\u00A0${symbol}`;

	return {
		value: computedValue,
		clearedValue,
		sign,
		symbol,
		endPos: computedValue.length - 2,
	};
}

const isNumber = (value: any): value is number => typeof value === 'number';

const isPriceRange = (value: number[]): value is [number, number] => {
	return value.length === 2 && isNumber(value[0]) && isNumber(value[1]);
};

const range = {
	parse: function (data: string) {
		const range = data.split(':').map(Number);
		return isPriceRange(range) ? range : undefined;
	},
	stringify: function (priceRange: number[]) {
		return priceRange.length === 2 ? priceRange.join(':') : undefined;
	},
};

function calculateLoanFactor(months: number, monthlyPercentage: number) {
	const p = monthlyPercentage / 100;
	const x = Math.pow(1 + p, months);

	return p + p / (x - 1);
}

export function calculateLoanMonthlyPayment(amount: number, months: number, monthlyPercentage: number) {
	const k = calculateLoanFactor(months, monthlyPercentage);

	return amount * k;
}

export const money = {
	format,
	currencySymbolByCode,
	range,
	toNumber,
	convertToSubunits,
	convertToBaseCurrency,
};
